import { useState, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Shortlist, Player } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { useDataCoordinator } from "@/hooks/useDataCoordinator";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { lazy, Suspense } from "react";

// Dynamically import heavy components  
const PlayerDetailModal = lazy(() => import("@/components/players/PlayerDetailModal"));
import { useUnitsPreference } from "@/hooks/useUnitsPreference";
import { convertHeight, convertWeight } from "@/lib/unitConversion";
import { exportPlayersToCSV, generateCSVFilename, type ColumnConfig } from "@/lib/csvExport";
import { usePlayerSelection } from "@/hooks/usePlayerSelection";
import { MultiSelect, OptionType } from "@/components/ui/multi-select";
import { countries, playingLevels, mainPositions, specificPositions } from "@/lib/filter-data";
import BulkEmailModal from "@/components/players/BulkEmailModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Column configuration using shared interface
const AVAILABLE_COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Name', defaultVisible: true, alwaysVisible: true },
  { key: 'age', label: 'Age', defaultVisible: true },
  { key: 'position', label: 'Position', defaultVisible: true },
  { key: 'specificPositions', label: 'Specific Positions', defaultVisible: true },
  { key: 'height', label: 'Height', defaultVisible: true },
  { key: 'weight', label: 'Weight', defaultVisible: true },
  { key: 'playingLevel', label: 'League Level', defaultVisible: true },
  { key: 'gpa', label: 'GPA', defaultVisible: true },
  { key: 'hsGradYear', label: 'HS Grad Year', defaultVisible: true },
  { key: 'currentClub', label: 'Current Club', defaultVisible: false },
  { key: 'nationality', label: 'Nationality', defaultVisible: false },
  { key: 'internationalCaps', label: 'International Caps', defaultVisible: false },
  { key: 'previousClubs', label: 'Previous Clubs', defaultVisible: false },
  { key: 'universityGradYear', label: 'University Grad Year', defaultVisible: false },
  { key: 'jerseyNumber', label: 'Jersey Number', defaultVisible: false },
  { key: 'hsGradYearInEmail', label: 'HS Grad Year in Email', defaultVisible: false },
  { key: 'universityGradYearInEmail', label: 'Univ Grad Year in Email', defaultVisible: false },
  { key: 'event', label: 'Event(s)', defaultVisible: false },
];

interface ShortlistDetailProps {
  shortlist: Shortlist;
  onBack: () => void;
}

export default function ShortlistDetail({ shortlist, onBack }: ShortlistDetailProps) {
  const { toast } = useToast();
  const { isMetric } = useUnitsPreference();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  
  // Advanced filtering state - removed old filters object
  // const [filters, setFilters] = useState<Record<string, string>>({});
  const [includeMissingValues, setIncludeMissingValues] = useState(true);
  
  // Advanced filters (copied from PlayerFilters)
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedSpecificPositions, setSelectedSpecificPositions] = useState<string[]>([]);
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [ageRange, setAgeRange] = useState<[number, number]>([15, 22]);
  const [heightRange, setHeightRange] = useState<[number, number]>([150, 210]); // cm
  const [weightRange, setWeightRange] = useState<[number, number]>([50, 100]); // kg
  const [gpaRange, setGpaRange] = useState<[number, number]>([1.0, 5.0]);
  const [hsGradYearRange, setHsGradYearRange] = useState<[number, number]>([2024, 2028]);
  const [internationalCaps, setInternationalCaps] = useState("any");
  const [activeFilterTracking, setActiveFilterTracking] = useState({
    age: false,
    height: false,
    weight: false,
    gpa: false,
    hsGradYear: false
  });
  
  // Use shared player selection hook - must be called unconditionally
  const {
    selectedPlayerIds,
    handleSelectPlayer,
    handleSelectAll,
    handleClearSelection,
    isPlayerSelected,
    areAllSelected,
    areSomeSelected,
    getSelectionCount
  } = usePlayerSelection({
    clearOnChange: [
      searchTerm,
      selectedPositions,
      selectedSpecificPositions,
      selectedNationalities,
      selectedLevels,
      selectedEvents,
      ageRange,
      heightRange,
      weightRange,
      gpaRange,
      hsGradYearRange,
      internationalCaps,
      activeFilterTracking,
      includeMissingValues
    ]
  });
  const [useImperialHeight, setUseImperialHeight] = useState(true);
  const [useImperialWeight, setUseImperialWeight] = useState(true);
  const [activeTab, setActiveTab] = useState("basic");
  
  // Slider wrapper functions to handle type conversion
  const handleAgeRangeChange = (value: number[]) => {
    setAgeRange([value[0], value[1]]);
  };
  
  const handleHeightRangeChange = (value: number[]) => {
    setHeightRange([value[0], value[1]]);
  };
  
  const handleWeightRangeChange = (value: number[]) => {
    setWeightRange([value[0], value[1]]);
  };
  
  const handleGpaRangeChange = (value: number[]) => {
    setGpaRange([value[0], value[1]]);
  };
  
  const handleHsGradYearRangeChange = (value: number[]) => {
    setHsGradYearRange([value[0], value[1]]);
  };
  
  // Filter options (copied from PlayerFilters) - moved here to be defined before use
  const positionOptions: OptionType[] = [
    { label: "All", value: "all" },
    ...mainPositions.map(pos => ({ label: pos, value: pos }))
  ];
  
  const specificPositionOptions: OptionType[] = [
    { label: "All", value: "all" },
    ...specificPositions.map(pos => ({ label: pos, value: pos }))
  ];
  
  const nationalityOptions: OptionType[] = [
    { label: "All Nationalities", value: "all" },
    ...countries.map(country => ({ label: country, value: country }))
  ];
  
  // Get all players from data coordinator for dynamic playing levels
  const { players: allPlayers, shortlists } = useDataCoordinator();
  
  // Convert playing levels array to options format - dynamically includes levels from player database
  const levelOptions: OptionType[] = useMemo(() => {
    // Extract unique playing levels from the coach's player database
    const uniquePlayingLevelsFromDatabase = Array.from(
      new Set(
        allPlayers
          .map(player => player.playing_level)
          .filter((level): level is string => level != null && level.trim() !== "") // Type guard to ensure string
      )
    );
    
    // Combine predefined playing levels with unique levels from database
    const allPlayingLevels = Array.from(
      new Set([...playingLevels, ...uniquePlayingLevelsFromDatabase])
    ).sort(); // Sort alphabetically for consistent ordering
    
    return [
      { label: "All Levels", value: "all" },
      ...allPlayingLevels.map(level => ({ label: level, value: level }))
    ];
  }, [allPlayers]);

  // Add event options for filtering
  const eventOptions: OptionType[] = [
    { label: "All Events", value: "all" },
    ...Array.from(new Set(allPlayers.flatMap(player => Array.isArray(player.event) ? player.event : [])))
      .filter(e => e && e.trim() !== "")
      .map(event => ({ label: event, value: event }))
  ];
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    // Load from localStorage or use defaults (specific to this shortlist)
    const saved = localStorage.getItem(`shortlistColumns-${shortlist.id}`);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        // Fall back to defaults if parsing fails
      }
    }
    return new Set(AVAILABLE_COLUMNS.filter(col => col.defaultVisible).map(col => col.key));
  });
  
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem(`shortlistColumns-${shortlist.id}`, JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns, shortlist.id]);
  
  // Shared selection event handlers for checkbox integration
  const handleSelectPlayerChange = (playerId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent opening player modal
    handleSelectPlayer(playerId, event.target.checked);
  };
  
  // Helper functions for height/weight display (copied from PlayerFilters)
  const cmToFeetInches = (cm: number): string => {
    const inches = Math.round(cm / 2.54);
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return `${feet}'${remainingInches}"`;
  };

  const displayHeight = (range: [number, number]): string => {
    if (useImperialHeight) {
      return `${cmToFeetInches(range[0])} - ${cmToFeetInches(range[1])}`;
    }
    return `${range[0]} - ${range[1]} cm`;
  };

  const displayWeight = (range: [number, number]): string => {
    if (useImperialWeight) {
      return `${Math.round(range[0] * 2.20462)} - ${Math.round(range[1] * 2.20462)} lbs`;
    }
    return `${range[0]} - ${range[1]} kg`;
  };
  
  const toggleHeightUnits = () => {
    setUseImperialHeight(!useImperialHeight);
  };

  const toggleWeightUnits = () => {
    setUseImperialWeight(!useImperialWeight);
  };
  
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedPositions([]);
    setSelectedSpecificPositions([]);
    setSelectedNationalities([]);
    setSelectedLevels([]);
    setSelectedEvents([]);
    setAgeRange([15, 22]);
    setHeightRange([150, 210]);
    setWeightRange([50, 100]);
    setGpaRange([1.0, 5.0]);
    setHsGradYearRange([2024, 2028]);
    setInternationalCaps("any");
    setActiveFilterTracking({
      age: false,
      height: false,
      weight: false,
      gpa: false,
      hsGradYear: false
    });
    setIncludeMissingValues(true);
  };
  
  // Use data coordinator for unified data access (already called above for players)
  // const { shortlists: allShortlists } = useDataCoordinator();
  
  // Get shortlist with players
  const { data: shortlistWithPlayers, isLoading, error } = useQuery({
    queryKey: [`/api/shortlists/${shortlist.id}`],
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 1;
    },
    retryDelay: 1000,
  });
  
  // Advanced filtering logic (similar to PlayerTable) - memoized for performance
  const filterPlayers = useCallback((players: any[]) => {
    return players.filter((player: any) => {
      // First check for missing values if includeMissingValues is false
      if (!includeMissingValues) {
        // Check for active filters
        const hasActivePositionFilters = selectedPositions.length > 0 && !selectedPositions.includes('all');
        const hasActiveSpecificPositionFilters = selectedSpecificPositions.length > 0 && !selectedSpecificPositions.includes('all');
        const hasActiveNationalityFilters = selectedNationalities.length > 0 && !selectedNationalities.includes('all');
        const hasActiveLevelFilters = selectedLevels.length > 0 && !selectedLevels.includes('all');
        const hasActiveRangeFilters = Object.values(activeFilterTracking).some(Boolean);
        
        const hasActiveFilters = hasActivePositionFilters || hasActiveSpecificPositionFilters || 
                                hasActiveNationalityFilters || hasActiveLevelFilters || 
                                hasActiveRangeFilters || internationalCaps !== 'any';
        
        if (hasActiveFilters) {
          // When filters are applied, only check for missing values in actively filtered fields
          if (hasActivePositionFilters && (!player.position || player.position.length === 0)) return false;
          if (hasActiveSpecificPositionFilters && (!player.specific_positions || player.specific_positions.length === 0)) return false;
          if (hasActiveNationalityFilters && !player.nationality) return false;
          if (hasActiveLevelFilters && !player.playing_level) return false;
          if (activeFilterTracking.height && !player.height) return false;
          if (activeFilterTracking.weight && !player.weight) return false;
          if (activeFilterTracking.gpa && !player.gpa) return false;
          if (activeFilterTracking.age && (player.age === null || player.age === undefined)) return false;
          if (activeFilterTracking.hsGradYear && !player.hs_grad_year) return false;
          if (internationalCaps !== 'any') {
            const hasCapsData = (player.international_caps && Array.isArray(player.international_caps) && player.international_caps.length > 0) || 
                               (player.distinguishments && Array.isArray(player.distinguishments) && player.distinguishments.length > 0);
            if (!hasCapsData) return false;
          }
        } else {
          // When no filters are applied, exclude players with missing values in currently visible columns
          if (visibleColumns.has('position') && (!player.position || player.position.length === 0)) return false;
          if (visibleColumns.has('nationality') && !player.nationality) return false;
          if (visibleColumns.has('playingLevel') && !player.playing_level) return false;
          if (visibleColumns.has('currentClub') && !player.current_club) return false;
          if (visibleColumns.has('height') && !player.height) return false;
          if (visibleColumns.has('weight') && !player.weight) return false;
          if (visibleColumns.has('age') && (player.age === null || player.age === undefined)) return false;
          if (visibleColumns.has('hsGradYear') && !player.hs_grad_year) return false;
          if (visibleColumns.has('gpa') && !player.gpa) return false;
          if (visibleColumns.has('specificPositions') && (!player.specific_positions || player.specific_positions.length === 0)) return false;
          if (visibleColumns.has('internationalCaps')) {
            const hasCapsData = (player.international_caps && Array.isArray(player.international_caps) && player.international_caps.length > 0) || 
                               (player.distinguishments && Array.isArray(player.distinguishments) && player.distinguishments.length > 0);
            if (!hasCapsData) return false;
          }
          if (visibleColumns.has('previousClubs') && (!player.previous_clubs || player.previous_clubs.length === 0)) return false;
          if (visibleColumns.has('universityGradYear') && !player.university_grad_year) return false;
          if (visibleColumns.has('jerseyNumber') && !player.jersey_number) return false;
          if (visibleColumns.has('hsGradYearInEmail') && (player.hs_grad_year_in_email === null || player.hs_grad_year_in_email === undefined)) return false;
          if (visibleColumns.has('universityGradYearInEmail') && (player.university_grad_year_in_email === null || player.university_grad_year_in_email === undefined)) return false;
        }
      }
      
      // Search term filtering
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          (player.name && player.name.toLowerCase().includes(searchLower)) ||
          (player.position && player.position.some((pos: string) => pos.toLowerCase().includes(searchLower))) ||
          (player.nationality && player.nationality.toLowerCase().includes(searchLower)) ||
          (player.current_club && player.current_club.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }
      
      // Position filtering
      if (
        selectedPositions.length > 0 &&
        !selectedPositions.includes('all')
      ) {
        // Handle both array and string for player.position
        const playerPositions: string[] = Array.isArray(player.position)
          ? player.position
          : typeof player.position === "string" && player.position.trim() !== ""
            ? [player.position]
            : [];

        if (
          playerPositions.length === 0 ||
          playerPositions.every((pos: string) => pos === "N/A" || !pos || !pos.trim())
        ) {
          return false;
        }

        const hasMatch = selectedPositions.some(selectedPos =>
          playerPositions.some(
            (playerPos: string) =>
              playerPos &&
              playerPos !== "N/A" &&
              playerPos.toLowerCase().includes(selectedPos.toLowerCase())
          )
        );
        if (!hasMatch) return false;
      }
      
      // Specific position filtering
      if (
        selectedSpecificPositions.length > 0 &&
        !selectedSpecificPositions.includes('all')
      ) {
        if (!Array.isArray(player.specific_positions) || player.specific_positions.length === 0 || player.specific_positions.every((pos: string) => pos === "N/A" || !pos || !pos.trim())) {
          return false;
        }
        const positionMatches = selectedSpecificPositions.some(specificPos =>
          player.specific_positions.some((playerPos: string) =>
            playerPos &&
            playerPos !== "N/A" &&
            playerPos.toLowerCase().includes(specificPos.toLowerCase())
          )
        );
        if (!positionMatches) return false;
      }
      
      // Nationality filtering
      if (
        selectedNationalities.length > 0 &&
        !selectedNationalities.includes('all')
      ) {
        if (!player.nationality || player.nationality === "N/A" || !player.nationality.trim()) return false;
        const hasMatch = selectedNationalities.some(nat =>
          player.nationality &&
          player.nationality !== "N/A" &&
          player.nationality.toLowerCase().includes(nat.toLowerCase())
        );
        if (!hasMatch) return false;
      }
      
      // Playing level filtering
      if (
        selectedLevels.length > 0 &&
        !selectedLevels.includes('all')
      ) {
        if (!player.playing_level || player.playing_level === "N/A" || !player.playing_level.trim()) return false;
        const hasMatch = selectedLevels.some(level =>
          player.playing_level &&
          player.playing_level !== "N/A" &&
          player.playing_level.toLowerCase().includes(level.toLowerCase())
        );
        if (!hasMatch) return false;
      }

      // Event filtering
      if (
        selectedEvents.length > 0 &&
        !selectedEvents.includes('all')
      ) {
        if (
          !Array.isArray(player.event) ||
          player.event.length === 0 ||
          player.event.every((ev: string) => ev === "N/A" || !ev || !ev.trim())
        ) {
          return false;
        }
        const hasMatch = selectedEvents.some(filterEvent =>
          player.event.some((playerEvent: string) =>
            playerEvent &&
            playerEvent !== "N/A" &&
            playerEvent.toLowerCase().includes(filterEvent.toLowerCase())
          )
        );
        if (!hasMatch) return false;
      }
      
      // Age filtering
      if (activeFilterTracking.age && player.age !== null && player.age !== undefined) {
        if (player.age < ageRange[0] || player.age > ageRange[1]) return false;
      }
      
      // Height filtering
      if (activeFilterTracking.height && player.height) {
        let playerHeight = 0;
        
        // Extract height value from string (e.g. "6'4\"" or "185 cm")
        if (player.height.includes("'")) {
          // Height is in feet/inches - handle various formats
          const heightStr = player.height.replace(/"/g, '').trim(); // Remove quotes and trim
          const parts = heightStr.split("'");
          const feet = parseInt(parts[0]) || 0;
          const inches = parts[1] ? parseInt(parts[1]) || 0 : 0;
          const totalInches = feet * 12 + inches;
          playerHeight = Math.round(totalInches * 2.54); // Convert to cm
        } else if (player.height.includes("cm")) {
          // Height is in cm
          playerHeight = parseInt(player.height.replace(/[^\d]/g, '')) || 0;
        } else {
          // If just a number, assume it's in cm
          playerHeight = parseInt(player.height.replace(/[^\d]/g, '')) || 0;
        }
        
        if (playerHeight < heightRange[0] || playerHeight > heightRange[1]) return false;
      }
      
      // Weight filtering
      if (activeFilterTracking.weight && player.weight) {
        let playerWeight = 0;
        
        // Extract weight value from string (e.g. "180 lbs" or "82 kg")
        if (player.weight.includes("lbs") || player.weight.includes("lb")) {
          // Weight is in pounds - convert to kg
          const lbs = parseInt(player.weight.replace(/[^\d]/g, '')) || 0;
          playerWeight = Math.round(lbs / 2.20462);
        } else if (player.weight.includes("kg")) {
          // Weight is in kg
          playerWeight = parseInt(player.weight.replace(/[^\d]/g, '')) || 0;
        } else {
          // If just a number, assume it's in kg
          playerWeight = parseInt(player.weight.replace(/[^\d]/g, '')) || 0;
        }
        
        if (playerWeight < weightRange[0] || playerWeight > weightRange[1]) return false;
      }
      
      // GPA range filtering
      if (activeFilterTracking.gpa && player.gpa) {
        const gpa = parseFloat(player.gpa);
        if (gpa < gpaRange[0] || gpa > gpaRange[1]) return false;
      }
      
      // High school graduation year filtering
      if (activeFilterTracking.hsGradYear && player.hs_grad_year) {
        if (player.hs_grad_year < hsGradYearRange[0] || player.hs_grad_year > hsGradYearRange[1]) return false;
      }
      
      // International caps filtering
      if (internationalCaps !== "any") {
        // Check if player has international caps in the dedicated field
        const hasInternationalCaps = player.international_caps && 
                                    Array.isArray(player.international_caps) && 
                                    player.international_caps.length > 0;
        
        // Fallback to checking distinguishments field for backward compatibility
        const hasInternationalMention = player.distinguishments?.some((d: string) => 
          d.toLowerCase().includes("national team") || d.toLowerCase().includes("international")
        );
        
        const hasAnyInternationalExperience = hasInternationalCaps || hasInternationalMention;
        
        if (internationalCaps === "yes" && !hasAnyInternationalExperience) {
          return false;
        }
        
        if (internationalCaps === "no" && hasAnyInternationalExperience) {
          return false;
        }
      }
      
      return true;
    });
  }, [
    searchTerm,
    selectedPositions,
    selectedSpecificPositions,
    selectedNationalities,
    selectedLevels,
    selectedEvents,
    ageRange,
    heightRange,
    weightRange,
    gpaRange,
    hsGradYearRange,
    internationalCaps,
    activeFilterTracking,
    includeMissingValues,
    visibleColumns
  ]);
  
  // Extract players from query data
  const players = Array.isArray((shortlistWithPlayers as any)?.players)
    ? (shortlistWithPlayers as any).players
    : [];
  
  // Apply filtering - memoized for performance
  const filteredPlayers = useMemo(() => filterPlayers(players), [filterPlayers, players]);
  
  // Now we can define handleSelectAllFiltered since filteredPlayers is available
  const handleSelectAllFiltered = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const filteredPlayerIds = filteredPlayers.map(p => p.id);
    handleSelectAll(filteredPlayerIds, event.target.checked);
  };
  
  // Handle authentication errors
  if (error && (error.message?.includes('401') || error.message?.includes('403'))) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={onBack} className="px-2">
                ← Back to Shortlists
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-neutral-900 mt-2">{shortlist.name}</h1>
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-yellow-500 mb-2">
            <i className="fas fa-exclamation-triangle text-3xl"></i>
          </div>
          <h3 className="text-lg font-medium text-yellow-900 mb-2">Session Expired</h3>
          <p className="text-yellow-700 mb-4">
            Your session has expired. Please refresh the page to continue viewing your shortlist.
          </p>
          <div className="flex justify-center gap-3">
            <Button 
              onClick={() => window.location.reload()}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
            >
              <i className="fas fa-refresh mr-2"></i>
              Refresh Page
            </Button>
            <Button 
              variant="outline"
              onClick={onBack}
              className="px-4 py-2 rounded"
            >
              Back to Shortlists
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Remove player from shortlist mutation
  const removePlayerMutation = useMutation({
    mutationFn: async ({ shortlistId, playerId }: { shortlistId: number, playerId: number }) => {
      return await apiRequest("DELETE", `/api/shortlists/${shortlistId}/players/${playerId}`, {});
    },
    onMutate: async ({ playerId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [`/api/shortlists/${shortlist.id}`] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData([`/api/shortlists/${shortlist.id}`]);
      
      // Optimistically update to remove the player
      queryClient.setQueryData([`/api/shortlists/${shortlist.id}`], (old: any) => {
        if (!old?.players) return old;
        return {
          ...old,
          players: old.players.filter((player: any) => player.id !== playerId)
        };
      });
      
      return { previousData };
    },
    onSuccess: () => {
      // Refresh the data to ensure consistency
      queryClient.invalidateQueries({ queryKey: [`/api/shortlists/${shortlist.id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      toast({
        title: "Player removed",
        description: "Player has been removed from the shortlist.",
      });
    },
    onError: (_error, _variables, context) => {
      // Rollback the optimistic update
      if (context?.previousData) {
        queryClient.setQueryData([`/api/shortlists/${shortlist.id}`], context.previousData);
      }
      toast({
        title: "Error",
        description: "Failed to remove player from shortlist.",
        variant: "destructive",
      });
    },
  });

  // Bulk remove players mutation
  const bulkRemovePlayersMutation = useMutation({
    mutationFn: async ({ shortlistId, playerIds }: { shortlistId: number, playerIds: number[] }) => {
      // Remove players one by one (could be optimized with a bulk endpoint)
      const promises = playerIds.map(playerId => 
        apiRequest("DELETE", `/api/shortlists/${shortlistId}/players/${playerId}`, {})
      );
      await Promise.all(promises);
    },
    onMutate: async ({ playerIds }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [`/api/shortlists/${shortlist.id}`] });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData([`/api/shortlists/${shortlist.id}`]);
      
      // Optimistically update to remove the players
      queryClient.setQueryData([`/api/shortlists/${shortlist.id}`], (old: any) => {
        if (!old?.players) return old;
        return {
          ...old,
          players: old.players.filter((player: any) => !playerIds.includes(player.id))
        };
      });
      
      return { previousData };
    },
    onSuccess: (_, { playerIds }) => {
      // Clear selection and refresh data
      handleClearSelection();
      queryClient.invalidateQueries({ queryKey: [`/api/shortlists/${shortlist.id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      toast({
        title: "Players removed",
        description: `${playerIds.length} player(s) have been removed from the shortlist.`,
      });
    },
    onError: (_error, _variables, context) => {
      // Rollback the optimistic update
      if (context?.previousData) {
        queryClient.setQueryData([`/api/shortlists/${shortlist.id}`], context.previousData);
      }
      toast({
        title: "Error",
        description: "Failed to remove players from shortlist",
        variant: "destructive",
      });
    },
  });
  
  // Handle bulk remove players
  const handleBulkRemovePlayers = () => {
    const selectedIds = Array.from(selectedPlayerIds);
    if (selectedIds.length === 0) return;
    
    bulkRemovePlayersMutation.mutate({
      shortlistId: shortlist.id,
      playerIds: selectedIds
    });
  };

  // Delete shortlist mutation
  const deleteShortlistMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/shortlists/${shortlist.id}`, {});
      if (!response.ok) {
        let errorMessage = "Failed to delete shortlist";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          errorMessage = response.statusText || `HTTP Error ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      // Update the toast to show success
      toast({
        title: "Shortlist deleted",
        description: `"${shortlist.name}" has been successfully deleted.`,
      });
      
      // Navigate back to shortlists list
      onBack();
      
      // Invalidate caches to update the shortlists list
      queryClient.invalidateQueries({ queryKey: ["/api/shortlists/"] });
      queryClient.invalidateQueries({ queryKey: [`/api/shortlists/${shortlist.id}`] });
    },
    onError: (error: any) => {
      console.error("Delete shortlist error:", error);
      // Show error toast
      toast({
        title: "Error",
        description: `Failed to delete shortlist: ${error.message || 'Please try again'}`,
        variant: "destructive",
      });
    },
  });

  const handlePlayerClick = async (player: Player) => {
    // Fetch the latest player data to ensure we have the most up-to-date notes
    try {
      const response = await apiRequest("GET", `/api/players/${player.id}`);
      if (response.ok) {
        const updatedPlayer = await response.json();
        setSelectedPlayer(updatedPlayer);
      } else {
        // If fetch fails (including auth errors), use the player data we already have
        console.warn(`Failed to fetch latest player data: ${response.status} ${response.statusText}`);
        setSelectedPlayer(player);
      }
    } catch (error) {
      console.error("Failed to fetch latest player data:", error);
      // Use the player data we already have
      setSelectedPlayer(player);
    }
    setShowDetailModal(true);
  };

  const handleRemovePlayer = (playerId: number) => {
    removePlayerMutation.mutate({ shortlistId: shortlist.id, playerId });
  };

  const handleDeleteShortlist = () => {
    setIsDeleteDialogOpen(false);
    
    // Show deletion toast immediately
    toast({
      title: "Deleting shortlist...",
      description: `Removing "${shortlist.name}"...`,
    });
    
    // Perform the deletion - navigation will happen on success
    deleteShortlistMutation.mutate();
  };

  // Column customization handlers
  const handleToggleColumn = (columnKey: string) => {
    const newVisibleColumns = new Set(visibleColumns);
    if (newVisibleColumns.has(columnKey)) {
      // Don't allow hiding always-visible columns
      const column = AVAILABLE_COLUMNS.find(col => col.key === columnKey);
      if (column?.alwaysVisible) return;
      
      newVisibleColumns.delete(columnKey);
      
      // When hiding a column, reset any related filters
      resetFiltersForColumn(columnKey);
    } else {
      newVisibleColumns.add(columnKey);
    }
    setVisibleColumns(newVisibleColumns);
  };

  // Helper function to reset filters for a specific column
  const resetFiltersForColumn = (columnKey: string) => {
    switch (columnKey) {
      case 'position':
        setSelectedPositions([]);
        break;
      case 'specificPositions':
        setSelectedSpecificPositions([]);
        break;
      case 'nationality':
        setSelectedNationalities([]);
        break;
      case 'playingLevel':
        setSelectedLevels([]);
        break;
      case 'event':
        setSelectedEvents([]);
        break;
      case 'age':
        setAgeRange([15, 22]);
        setActiveFilterTracking(prev => ({ ...prev, age: false }));
        break;
      case 'height':
        setHeightRange([150, 210]);
        setActiveFilterTracking(prev => ({ ...prev, height: false }));
        break;
      case 'weight':
        setWeightRange([50, 100]);
        setActiveFilterTracking(prev => ({ ...prev, weight: false }));
        break;
      case 'gpa':
        setGpaRange([1.0, 5.0]);
        setActiveFilterTracking(prev => ({ ...prev, gpa: false }));
        break;
      case 'hsGradYear':
        setHsGradYearRange([2024, 2028]);
        setActiveFilterTracking(prev => ({ ...prev, hsGradYear: false }));
        break;
      case 'internationalCaps':
        setInternationalCaps("any");
        break;
    }
  };

  const handleResetColumns = () => {
    const defaultColumns = new Set(AVAILABLE_COLUMNS.filter(col => col.defaultVisible).map(col => col.key));
    setVisibleColumns(defaultColumns);
    
    // When resetting columns, also reset all filters
    handleResetFilters();
  };

  // CSV Export using shared utility
  const handleExportCSV = () => {
    exportPlayersToCSV({
      players: filteredPlayers,
      visibleColumns,
      columnConfigs: AVAILABLE_COLUMNS,
      isMetric,
      filename: generateCSVFilename('shortlist', shortlist.name),
      successMessage: `Exported ${filteredPlayers.length} players from "${shortlist.name}"`,
      toastFunction: toast
    });
  };

  // Helper function to render table headers
  const renderTableHeader = (columnKey: string) => {
    const column = AVAILABLE_COLUMNS.find(col => col.key === columnKey);
    if (!column || !visibleColumns.has(columnKey)) return null;

    return (
      <th key={columnKey} scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
        {column.label}
      </th>
    );
  };

  // Helper function to render table cells
  const renderTableCell = (player: any, columnKey: string) => {
    if (!visibleColumns.has(columnKey)) return null;

    const getCellContent = () => {
      switch (columnKey) {
        case 'name':
          return (
            <div className="flex items-center">
              <AvatarPlaceholder name={player.name} className="h-10 w-10"/>
              <div className="ml-4">
                <div className="text-sm font-medium text-neutral-900">{player.name}</div>
                {player.current_club && (
                  <div className="text-sm text-neutral-500">{player.current_club}</div>
                )}
              </div>
            </div>
          );
        case 'age':
          return <div className="text-sm text-neutral-900">{player.age || "—"}</div>;
        case 'position':
          return (
            <div className="text-sm text-neutral-900">
              {Array.isArray(player.position)
                ? (player.position.length > 0 ? player.position.join(", ") : "—")
                : (typeof player.position === "string" && player.position.trim() !== "" ? player.position : "—")}
            </div>
          );
        case 'specificPositions':
          return <div className="text-sm text-neutral-900">{player.specific_positions?.join(", ") || "—"}</div>;
        case 'height':
          return <div className="text-sm text-neutral-900">{convertHeight(player.height, isMetric) || "—"}</div>;
        case 'weight':
          return <div className="text-sm text-neutral-900">{convertWeight(player.weight, isMetric) || "—"}</div>;
        case 'nationality':
          return <div className="text-sm text-neutral-900">{player.nationality || "—"}</div>;
        case 'internationalCaps':
          return (
            <div className="text-sm text-neutral-900">
              {player.international_caps && player.international_caps.length > 0
                ? player.international_caps
                    .filter((cap: any) => cap && typeof cap === 'object') // Filter out invalid cap objects
                    .map((cap: any, index: number) => {
                      // Handle the actual data structure: [{"U15":"3"},{"U16":"3"}]
                      return Object.entries(cap).map(([level, caps]) => `${level}: ${caps}`);
                    })
                    .flat() // Flatten the array since we're mapping over entries
                    .join(', ')
                : 'None'}
            </div>
          );
        case 'playingLevel':
          return <div className="text-sm text-neutral-900">{player.playing_level || "—"}</div>;
        case 'gpa':
          return <div className="text-sm text-neutral-900">{player.gpa || "—"}</div>;
        case 'hsGradYear':
          return <div className="text-sm text-neutral-900">{player.hs_grad_year || "—"}</div>;
        case 'currentClub':
          return <div className="text-sm text-neutral-900">{player.current_club || "—"}</div>;
        case 'previousClubs':
          return (
            <div className="text-sm text-neutral-900">
              {player.previous_clubs && player.previous_clubs.length > 0
                ? player.previous_clubs.join(', ')
                : 'N/A'}
            </div>
          );
        case 'universityGradYear':
          return <div className="text-sm text-neutral-900">{player.university_grad_year || 'N/A'}</div>;
        case 'jerseyNumber':
          return <div className="text-sm text-neutral-900">{player.jersey_number || 'N/A'}</div>;
        case 'hsGradYearInEmail':
          return (
            <div className="text-sm text-neutral-900">
              {player.hs_grad_year_in_email === true ? (
                <span className="text-green-600 font-medium">Yes</span>
              ) : player.hs_grad_year_in_email === false ? (
                <span className="text-red-600 font-medium">No</span>
              ) : (
                'N/A'
              )}
            </div>
          );
        case 'universityGradYearInEmail':
          return (
            <div className="text-sm text-neutral-900">
              {player.university_grad_year_in_email === true ? (
                <span className="text-green-600 font-medium">Yes</span>
              ) : player.university_grad_year_in_email === false ? (
                <span className="text-red-600 font-medium">No</span>
              ) : (
                'N/A'
              )}
            </div>
          );
        case 'event':
          return (
            <div className="text-sm text-neutral-900">
              {player.event && Array.isArray(player.event) && player.event.length > 0
                ? player.event.join(', ')
                : 'N/A'}
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <td key={columnKey} className="px-6 py-4 whitespace-nowrap">
        {getCellContent()}
      </td>
    );
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-neutral-200 rounded w-1/4 mb-4"></div>
        <div className="h-4 bg-neutral-200 rounded w-1/2 mb-8"></div>
        <div className="h-64 bg-neutral-100 rounded-lg"></div>
      </div>
    );
  }
  
  // Get selected players from filtered players
  const getSelectedPlayers = () => {
    return filteredPlayers.filter(player => selectedPlayerIds.has(player.id));
  };
  
  // Note: Selection state checks now handled by shared hook
  
  // Get active filters for display
  const getActiveFilters = (): Array<{ key: string; label: string; value: string }> => {
    const activeFilters: Array<{ key: string; label: string; value: string }> = [];
    
    if (searchTerm) {
      activeFilters.push({ key: 'search', label: 'Search', value: searchTerm });
    }
    
    if (selectedPositions.length > 0 && !selectedPositions.includes('all')) {
      activeFilters.push({ key: 'positions', label: 'Positions', value: selectedPositions.join(', ') });
    }
    
    if (selectedSpecificPositions.length > 0 && !selectedSpecificPositions.includes('all')) {
      activeFilters.push({ key: 'specificPositions', label: 'Specific Positions', value: selectedSpecificPositions.join(', ') });
    }
    
    if (selectedNationalities.length > 0 && !selectedNationalities.includes('all')) {
      activeFilters.push({ key: 'nationalities', label: 'Nationalities', value: selectedNationalities.join(', ') });
    }
    
    if (selectedLevels.length > 0 && !selectedLevels.includes('all')) {
      activeFilters.push({ key: 'levels', label: 'Playing Levels', value: selectedLevels.join(', ') });
    }

    if (selectedEvents.length > 0 && !selectedEvents.includes('all')) {
      activeFilters.push({ key: 'events', label: 'Events', value: selectedEvents.join(', ') });
    }
    
    if (activeFilterTracking.age) {
      activeFilters.push({ key: 'age', label: 'Age', value: `${ageRange[0]} - ${ageRange[1]}` });
    }
    
    if (activeFilterTracking.height) {
      activeFilters.push({ key: 'height', label: 'Height', value: displayHeight(heightRange) });
    }
    
    if (activeFilterTracking.weight) {
      activeFilters.push({ key: 'weight', label: 'Weight', value: displayWeight(weightRange) });
    }
    
    if (activeFilterTracking.gpa) {
      activeFilters.push({ key: 'gpa', label: 'GPA', value: `${gpaRange[0]} - ${gpaRange[1]}` });
    }
    
    if (activeFilterTracking.hsGradYear) {
      activeFilters.push({ key: 'hsGradYear', label: 'HS Grad Year', value: `${hsGradYearRange[0]} - ${hsGradYearRange[1]}` });
    }
    
    if (internationalCaps !== 'any') {
      activeFilters.push({ key: 'internationalCaps', label: 'International Caps', value: internationalCaps === 'yes' ? 'Has Caps' : 'No Caps' });
    }
    
    if (!includeMissingValues) {
      activeFilters.push({ key: 'includeMissingValues', label: 'Hide Missing Values', value: 'Enabled' });
    }
    
    return activeFilters;
  };
  
  const activeFilters = getActiveFilters();

  return (
    <div className="space-y-6">
    {/* Header */}
    <div className="space-y-4">
      {/* Title Section */}
      <div>
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="px-2">
            ← Back to Shortlists
          </Button>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mt-2">{shortlist.name}</h1>
        <p className="text-neutral-600 mt-1">
          {filteredPlayers.length} {filteredPlayers.length === 1 ? 'player' : 'players'}
          {getSelectionCount() > 0 && ` · ${getSelectionCount()} selected`}
        </p>
      </div>
      
      {/* Action Buttons - Stack on mobile, flex on larger screens */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:items-center gap-2 lg:gap-3">
        {/* Selection controls */}
        {getSelectionCount() > 0 && (
          <>
            <Button
              variant="outline" 
              size="sm"
              onClick={() => {
                const selectedPlayers = getSelectedPlayers();
                if (selectedPlayers.length === 0) {
                  toast({
                    title: "No players selected",
                    description: "Please select players to email.",
                    variant: "destructive",
                  });
                  return;
                }
                
                const playersWithEmails = selectedPlayers.filter((player: Player) => player.email);
                if (playersWithEmails.length === 0) {
                  toast({
                    title: "No email addresses found",
                    description: "None of the selected players have email addresses.",
                    variant: "destructive",
                  });
                  return;
                }
                
                setShowBulkEmailModal(true);
              }}
              className="flex items-center gap-2"
            >
              <i className="fas fa-envelope"></i>
              Email Selected ({getSelectionCount()})
            </Button>
            
            <Button
              variant="destructive" 
              size="sm"
              onClick={handleBulkRemovePlayers}
              disabled={bulkRemovePlayersMutation.isPending}
              className="flex items-center gap-2"
            >
              {bulkRemovePlayersMutation.isPending ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-trash"></i>
              )}
              Remove Selected ({getSelectionCount()})
            </Button>
            
            <Button
              variant="ghost" 
              size="sm"
              onClick={handleClearSelection}
              className="text-neutral-500 col-span-2 sm:col-span-1"
            >
              Clear Selection
            </Button>
          </>
        )}
        
        <Button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
          <i className="fas fa-filter mr-2"></i> Filters
        </Button>
        
        <Button variant="outline" onClick={() => setShowColumnCustomizer(true)}>
          <i className="fas fa-columns mr-2"></i> Columns
        </Button>
        
        <Button variant="outline" onClick={handleExportCSV}>
          <i className="fas fa-file-csv mr-2"></i> Export CSV
        </Button>
        
        <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
          <i className="fas fa-trash mr-2"></i> Delete
        </Button>
      </div>
    </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="bg-white rounded-lg border border-neutral-200 shadow-sm">
          <div className="p-6 border-b border-neutral-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-neutral-900">Filters</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="include-missing"
                    checked={includeMissingValues}
                    onCheckedChange={setIncludeMissingValues}
                  />
                  <Label htmlFor="include-missing" className="text-sm text-neutral-600">
                    Include missing values
                  </Label>
                </div>
                <Button variant="outline" size="sm" onClick={handleResetFilters}>
                  Clear All
                </Button>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="advanced">Range</TabsTrigger>
                <TabsTrigger value="other">Other</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-6">
                {/* Search */}
                <div>
                  <Label htmlFor="search" className="text-sm font-medium text-neutral-700 mb-2 block">Search</Label>
                  <Input
                    id="search"
                    type="text"
                    placeholder="Search by name, position, nationality, or current club..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                  {/* Position Filter */}
                  <div>
                    <Label className="text-sm font-medium text-neutral-700 mb-2 block">Position</Label>
                    <MultiSelect
                      options={positionOptions}
                      selected={selectedPositions}
                      onChange={setSelectedPositions}
                      placeholder="All positions"
                      className="w-full"
                    />
                  </div>
                  
                  {/* Specific Position Filter */}
                  <div>
                    <Label className="text-sm font-medium text-neutral-700 mb-2 block">Specific Position</Label>
                    <MultiSelect
                      options={specificPositionOptions}
                      selected={selectedSpecificPositions}
                      onChange={setSelectedSpecificPositions}
                      placeholder="All specific positions"
                      className="w-full"
                    />
                  </div>
                  
                  {/* Nationality Filter */}
                  <div>
                    <Label className="text-sm font-medium text-neutral-700 mb-2 block">Nationality</Label>
                    <MultiSelect
                      options={nationalityOptions}
                      selected={selectedNationalities}
                      onChange={setSelectedNationalities}
                      placeholder="All nationalities"
                      className="w-full"
                    />
                  </div>
                  
                  {/* Playing Level Filter */}
                  <div>
                    <Label className="text-sm font-medium text-neutral-700 mb-2 block">Playing Level</Label>
                    <MultiSelect
                      options={levelOptions}
                      selected={selectedLevels}
                      onChange={setSelectedLevels}
                      placeholder="All levels"
                      className="w-full"
                    />
                  </div>
                </div>
                {/* Add event filter */}
                <div>
                  <Label htmlFor="event-filter" className="text-sm font-medium text-neutral-700 mb-2 block">Events</Label>
                  <MultiSelect
                    options={eventOptions}
                    selected={selectedEvents}
                    onChange={setSelectedEvents}
                    placeholder="Filter by event(s)"
                    className="w-full"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="advanced" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {/* Age Range Card */}
                  <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium text-neutral-700">Age Range</Label>
                      <Switch
                        checked={activeFilterTracking.age}
                        onCheckedChange={(checked) => setActiveFilterTracking(prev => ({ ...prev, age: checked }))}
                      />
                    </div>
                    {activeFilterTracking.age ? (
                      <div className="space-y-3">
                        <Slider
                          value={ageRange}
                          onValueChange={handleAgeRangeChange}
                          min={15}
                          max={30}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>{ageRange[0]} years</span>
                          <span>{ageRange[1]} years</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-400">Click switch to enable</div>
                    )}
                  </div>
                  
                  {/* Height Range Card */}
                  <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium text-neutral-700">Height Range</Label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={toggleHeightUnits}
                          className={`px-2 py-1 text-xs rounded ${
                            useImperialHeight 
                              ? 'bg-primary text-white' 
                              : 'bg-neutral-200 text-neutral-600'
                          }`}
                        >
                          ft/in
                        </button>
                        <button
                          onClick={toggleHeightUnits}
                          className={`px-2 py-1 text-xs rounded ${
                            !useImperialHeight 
                              ? 'bg-primary text-white' 
                              : 'bg-neutral-200 text-neutral-600'
                          }`}
                        >
                          cm
                        </button>
                        <Switch
                          checked={activeFilterTracking.height}
                          onCheckedChange={(checked) => setActiveFilterTracking(prev => ({ ...prev, height: checked }))}
                        />
                      </div>
                    </div>
                    {activeFilterTracking.height ? (
                      <div className="space-y-3">
                        <Slider
                          value={heightRange}
                          onValueChange={handleHeightRangeChange}
                          min={140}
                          max={220}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>{useImperialHeight ? cmToFeetInches(heightRange[0]) : `${heightRange[0]} cm`}</span>
                          <span>{useImperialHeight ? cmToFeetInches(heightRange[1]) : `${heightRange[1]} cm`}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-400">Click switch to enable</div>
                    )}
                  </div>
                  
                  {/* Weight Range Card */}
                  <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium text-neutral-700">Weight Range</Label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={toggleWeightUnits}
                          className={`px-2 py-1 text-xs rounded ${
                            useImperialWeight 
                              ? 'bg-primary text-white' 
                              : 'bg-neutral-200 text-neutral-600'
                          }`}
                        >
                          lbs
                        </button>
                        <button
                          onClick={toggleWeightUnits}
                          className={`px-2 py-1 text-xs rounded ${
                            !useImperialWeight 
                              ? 'bg-primary text-white' 
                              : 'bg-neutral-200 text-neutral-600'
                          }`}
                        >
                          kg
                        </button>
                        <Switch
                          checked={activeFilterTracking.weight}
                          onCheckedChange={(checked) => setActiveFilterTracking(prev => ({ ...prev, weight: checked }))}
                        />
                      </div>
                    </div>
                    {activeFilterTracking.weight ? (
                      <div className="space-y-3">
                        <Slider
                          value={weightRange}
                          onValueChange={handleWeightRangeChange}
                          min={40}
                          max={120}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>
                            {useImperialWeight 
                              ? `${Math.round(weightRange[0] * 2.20462)} lbs` 
                              : `${weightRange[0]} kg`}
                          </span>
                          <span>
                            {useImperialWeight 
                              ? `${Math.round(weightRange[1] * 2.20462)} lbs` 
                              : `${weightRange[1]} kg`}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-400">Click switch to enable</div>
                    )}
                  </div>
                  
                  {/* GPA Range Card */}
                  <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium text-neutral-700">GPA Range</Label>
                      <Switch
                        checked={activeFilterTracking.gpa}
                        onCheckedChange={(checked) => setActiveFilterTracking(prev => ({ ...prev, gpa: checked }))}
                      />
                    </div>
                    {activeFilterTracking.gpa ? (
                      <div className="space-y-3">
                        <Slider
                          value={gpaRange}
                          onValueChange={handleGpaRangeChange}
                          min={1.0}
                          max={5.0}
                          step={0.1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>{gpaRange[0].toFixed(1)}</span>
                          <span>{gpaRange[1].toFixed(1)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-400">Click switch to enable</div>
                    )}
                  </div>
                  
                  {/* HS Grad Year Card */}
                  <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-sm font-medium text-neutral-700">HS Graduation Year</Label>
                      <Switch
                        checked={activeFilterTracking.hsGradYear}
                        onCheckedChange={(checked) => setActiveFilterTracking(prev => ({ ...prev, hsGradYear: checked }))}
                      />
                    </div>
                    {activeFilterTracking.hsGradYear ? (
                      <div className="space-y-3">
                        <Slider
                          value={hsGradYearRange}
                          onValueChange={handleHsGradYearRangeChange}
                          min={2020}
                          max={2030}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-neutral-500">
                          <span>{hsGradYearRange[0]}</span>
                          <span>{hsGradYearRange[1]}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-neutral-400">Click switch to enable</div>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="other" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* International Caps Card */}
                  <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                    <Label className="text-sm font-medium text-neutral-700 mb-3 block">International Experience</Label>
                    <Select value={internationalCaps} onValueChange={setInternationalCaps}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="yes">Has International Caps</SelectItem>
                        <SelectItem value="no">No International Caps</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}

      {/* Active Filters Bar */}
      {activeFilters.length > 0 && (
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-700">Active Filters:</span>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter) => (
                  <span
                    key={filter.key}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                  >
                    {filter.label}: {filter.value}
                  </span>
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="text-neutral-500 hover:text-neutral-700"
            >
              Clear All
            </Button>
          </div>
        </div>
      )}

      {/* Player Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        {filteredPlayers.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-neutral-400 mb-2">
              <i className="fas fa-users text-3xl"></i>
            </div>
            <h3 className="text-lg font-medium text-neutral-900 mb-1">No players found</h3>
            <p className="text-neutral-600">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            {/* Selection Controls */}
            <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={areAllSelected(filteredPlayers.map(p => p.id))}
                      ref={(el) => {
                        const filteredPlayerIds = filteredPlayers.map(p => p.id);
                        if (el) el.indeterminate = areSomeSelected(filteredPlayerIds) && !areAllSelected(filteredPlayerIds);
                      }}
                      onChange={handleSelectAllFiltered}
                      className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-neutral-600">
                      Select all {filteredPlayers.length} players
                    </span>
                  </div>
                  
                  {getSelectionCount() > 0 && (
                    <span className="text-sm text-neutral-600">
                      {getSelectionCount()} selected
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Select</span>
                    </th>
                    {renderTableHeader('name')}
                    {renderTableHeader('age')}
                    {renderTableHeader('position')}
                    {renderTableHeader('specificPositions')}
                    {renderTableHeader('height')}
                    {renderTableHeader('weight')}
                    {renderTableHeader('playingLevel')}
                    {renderTableHeader('nationality')}
                    {renderTableHeader('internationalCaps')}
                    {renderTableHeader('gpa')}
                    {renderTableHeader('hsGradYear')}
                    {renderTableHeader('previousClubs')}
                    {renderTableHeader('universityGradYear')}
                    {renderTableHeader('jerseyNumber')}
                    {renderTableHeader('hsGradYearInEmail')}
                    {renderTableHeader('universityGradYearInEmail')}
                    {renderTableHeader('currentClub')}
                    {renderTableHeader('event')}
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {filteredPlayers.map((player) => (
                    <tr 
                      key={player.id} 
                      className="hover:bg-neutral-50 cursor-pointer" 
                      onClick={() => handlePlayerClick(player)}
                    >
                      <td className="relative px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary"
                          checked={isPlayerSelected(player.id)}
                          onChange={(e) => handleSelectPlayerChange(player.id, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      {renderTableCell(player, 'name')}
                      {renderTableCell(player, 'age')}
                      {renderTableCell(player, 'position')}
                      {renderTableCell(player, 'specificPositions')}
                      {renderTableCell(player, 'height')}
                      {renderTableCell(player, 'weight')}
                      {renderTableCell(player, 'playingLevel')}
                      {renderTableCell(player, 'nationality')}
                      {renderTableCell(player, 'internationalCaps')}
                      {renderTableCell(player, 'gpa')}
                      {renderTableCell(player, 'hsGradYear')}
                      {renderTableCell(player, 'previousClubs')}
                      {renderTableCell(player, 'universityGradYear')}
                      {renderTableCell(player, 'jerseyNumber')}
                      {renderTableCell(player, 'hsGradYearInEmail')}
                      {renderTableCell(player, 'universityGradYearInEmail')}
                      {renderTableCell(player, 'currentClub')}
                      {renderTableCell(player, 'event')}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePlayer(player.id);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Player Detail Modal */}
      {selectedPlayer && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">Loading player details...</div>
        </div>}>
          <PlayerDetailModal
            player={selectedPlayer}
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedPlayer(null);
            }}
            shortlists={shortlists}
          />
        </Suspense>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shortlist</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{shortlist.name}"? This action cannot be undone.
              Players will be removed from this shortlist but will remain in your database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteShortlistMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteShortlist}
              disabled={deleteShortlistMutation.isPending}
            >
              {deleteShortlistMutation.isPending ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Deleting...
                </>
              ) : (
                "Delete Shortlist"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Column Customizer Modal */}
      <Dialog open={showColumnCustomizer} onOpenChange={setShowColumnCustomizer}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Customize Columns</DialogTitle>
            <DialogDescription>
              Choose which columns to display in this shortlist. Your preferences will be saved for "{shortlist.name}".
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-3 py-4 max-h-96 overflow-y-auto">
            {AVAILABLE_COLUMNS.map((column) => (
              <div key={column.key} className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id={`column-${column.key}`}
                  checked={visibleColumns.has(column.key)}
                  onChange={() => handleToggleColumn(column.key)}
                  disabled={column.alwaysVisible}
                  className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <label 
                  htmlFor={`column-${column.key}`}
                  className={`text-sm font-medium ${column.alwaysVisible ? 'text-neutral-400' : 'text-neutral-900 cursor-pointer'}`}
                >
                  {column.label}
                  {column.alwaysVisible && (
                    <span className="text-xs text-neutral-400 ml-1">(always visible)</span>
                  )}
                </label>
              </div>
            ))}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleResetColumns}
            >
              Reset to Default
            </Button>
            <Button
              onClick={() => setShowColumnCustomizer(false)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={showBulkEmailModal}
        onClose={() => setShowBulkEmailModal(false)}
        players={getSelectedPlayers()}
        title={`Email Selected Players from ${shortlist.name}`}
        subtitle={`Send email to ${getSelectionCount()} selected players`}
      />
    </div>
  );
} 